/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import '@/quote-management/components/ManageNegotiableQuote/ManageNegotiableQuote.css';
import { Skeleton, SkeletonRow } from '@adobe-commerce/elsie/components';

export interface ManageNegotiableQuoteProps extends Omit<HTMLAttributes<HTMLDivElement>, 'loading'> {
  loading?: boolean;
  quoteName: VNode;
  quoteStatus: VNode;
  banner?: VNode;
  details?: VNode;
  actionBar?: VNode;
  quoteContent: VNode;
  shippingInformationTitle: VNode;
  shippingInformation: VNode;
  quoteCommentsTitle: VNode;
  quoteComments: VNode;
  attachFilesField?: VNode;
  attachedFilesList?: VNode;
  footer: VNode;
}

export const ManageNegotiableQuote: FunctionComponent<ManageNegotiableQuoteProps> = ({
  className,
  loading,
  quoteName,
  quoteStatus,
  banner,
  details,
  actionBar,
  quoteContent,
  shippingInformationTitle,
  shippingInformation,
  quoteCommentsTitle,
  quoteComments,
  attachFilesField,
  attachedFilesList,
  footer,
  ...props
}) => {
  if (loading) {
    return <ManageNegotiableQuoteSkeleton />;
  }

  return (
    <div {...props} className={classes(['quote-management-manage-negotiable-quote', className])}>
      <div className={classes(['quote-management-manage-negotiable-quote__header'])}>
        {quoteName &&
          <VComponent
            node={quoteName}
            className={classes(['quote-management-manage-negotiable-quote__quote-name'])}
          />
        }
        {quoteStatus &&
          <VComponent
            node={quoteStatus}
            className={classes(['quote-management-manage-negotiable-quote__quote-status'])}
          />
        }
      </div>
      {banner &&
        <VComponent
          node={banner}
          className={classes(['quote-management-manage-negotiable-quote__banner'])}
        />
      }
      {details &&
        <VComponent
          node={details}
          className={classes(['quote-management-manage-negotiable-quote__details'])}
        />
      }
      {actionBar &&
        <VComponent
          node={actionBar}
          className={classes(['quote-management-manage-negotiable-quote__action-bar'])}
        />
      }
      {quoteContent &&
        <VComponent
          node={quoteContent}
          className={classes(['quote-management-manage-negotiable-quote__quote-content'])}
        />
      }
      <div data-testid="quote-shipping-information-container" className={classes(['quote-management-manage-negotiable-quote__shipping-information-container'])}>
        {shippingInformationTitle &&
          <VComponent
            node={shippingInformationTitle}
            className={classes(['quote-management-manage-negotiable-quote__shipping-information-title'])}
          />
        }
        {shippingInformation &&
          <VComponent
            node={shippingInformation}
            className={classes(['quote-management-manage-negotiable-quote__shipping-information'])}
          />
        }
      </div>
      <div data-testid="quote-comments-container" className={classes(['quote-management-manage-negotiable-quote__quote-comments-container'])}>
        {quoteCommentsTitle &&
          <VComponent
            node={quoteCommentsTitle}
            className={classes(['quote-management-manage-negotiable-quote__quote-comments-title'])}
          />
        }
        {quoteComments &&
          <VComponent
            node={quoteComments}
            className={classes(['quote-management-manage-negotiable-quote__quote-comments'])}
          />
        }
        {attachFilesField &&
          <VComponent
            node={attachFilesField}
            className={classes(['quote-management-manage-negotiable-quote__attach-files-field'])}
          />
        }
        {attachedFilesList &&
          <VComponent
            node={attachedFilesList}
            className={classes(['quote-management-manage-negotiable-quote__attached-files-list'])}
          />
        }
      </div>
      <VComponent
        node={footer}
        className={classes(['quote-management-manage-negotiable-quote__footer'])}
      />
    </div>
  );
};

export const ManageNegotiableQuoteSkeleton: FunctionComponent = () => {
  return (
    <Skeleton data-testid="manage-negotiable-quote-skeleton">
      <SkeletonRow variant="heading" fullWidth={true} size="large" />
      <SkeletonRow variant="row" fullWidth={false} size="small" />
      <SkeletonRow variant="row" fullWidth={false} size="small" />
      <SkeletonRow variant="row" fullWidth={true} />
      <SkeletonRow variant="row" fullWidth={true} size="xlarge" lines={2} />
      <SkeletonRow variant="row" fullWidth={false} size="small" />
    </Skeleton>
  );
};
